// OnlyFans Credentials Input Modal
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HttpsIcon from '@mui/icons-material/Https';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

export default function CredentialsModal({ open, onClose, onSubmit, loading }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setError('');
    onSubmit({ email, password });
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setPassword('');
      setError('');
      setShowPassword(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: '0 28px 60px rgba(24, 39, 75, 0.22)',
          m: { xs: 0, sm: 2 },
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle
        sx={{
          pt: { xs: 4, sm: 5 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 3, sm: 5 },
          background: `linear-gradient(140deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.18)} 100%)`,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.18),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2.5,
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.24)}`,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 60%)',
              opacity: 0.8,
            }}
          />
          <LockIcon 
            sx={{ 
              fontSize: 34, 
              color: theme.palette.primary.main,
              position: 'relative',
            }} 
          />
        </Box>
        
        <Typography 
          variant="h5" 
          fontWeight={700} 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1.35rem', sm: '1.65rem' },
            letterSpacing: '-0.01em',
            textAlign: 'center',
          }}
        >
          Enter OnlyFans Credentials
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '0.95rem' }, 
            px: { xs: 1, sm: 0 },
            maxWidth: 420,
            mx: 'auto',
            lineHeight: 1.6,
            textAlign: 'center',
          }}
        >
          We need your OnlyFans login to verify your account and fetch subscriptions
        </Typography>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          px: { xs: 3, sm: 5 }, 
          pt: { xs: 3, sm: 4 }, 
          pb: { xs: 2, sm: 3 },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} id="credentials-form">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              mb: 3,
              borderRadius: 3,
              mt: { xs: 1, sm: 1.5 },
              px: { xs: 2.5, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              background: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: alpha(theme.palette.primary.main, 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ShieldOutlinedIcon sx={{ color: theme.palette.primary.main }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Private & encrypted
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Your credentials are encrypted for transmission, used one-time for verification, and never stored.
              </Typography>
            </Box>
          </Box>

          <TextField
            autoFocus
            fullWidth
            label="OnlyFans Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            variant="outlined"
            sx={{ 
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                backgroundColor: alpha(theme.palette.common.black, 0.02),
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                '& fieldset': {
                  borderColor: alpha(theme.palette.text.primary, 0.1),
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&.Mui-focused': {
                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transform: 'translateY(-1px)',
                },
              },
            }}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ color: alpha(theme.palette.text.primary, 0.4) }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="OnlyFans Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            variant="outlined"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HttpsIcon sx={{ color: alpha(theme.palette.text.primary, 0.4) }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                backgroundColor: alpha(theme.palette.common.black, 0.02),
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                '& fieldset': {
                  borderColor: alpha(theme.palette.text.primary, 0.1),
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&.Mui-focused': {
                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transform: 'translateY(-1px)',
                },
              },
            }}
          />

          <Divider sx={{ my: { xs: 2, sm: 2.5 } }} />
          <Box
            sx={{
              mb: { xs: 2.5, sm: 3 },
              px: { xs: 2.5, sm: 3 },
              py: { xs: 1.5, sm: 1.75 },
              borderRadius: 3,
              background: alpha(theme.palette.warning.main, 0.06),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`,
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <HttpsIcon sx={{ color: theme.palette.warning.main, mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Password login required
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                We can only verify accounts signed in with an email and password. Google or X logins won’t work. If MFA is on, keep your device handy—we’ll ask for the code right away.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: { xs: 3, sm: 5 }, 
          pb: { xs: 3.5, sm: 4 }, 
          pt: { xs: 1.5, sm: 2.5 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          background: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading}
          fullWidth={isMobile}
          sx={{ 
            borderRadius: 2, 
            order: { xs: 2, sm: 1 },
            px: 3,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.primary, 0.05),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="credentials-form"
          variant="contained"
          disabled={loading || !email || !password}
          fullWidth={isMobile}
          sx={{
            px: 4,
            borderRadius: 2,
            order: { xs: 1, sm: 2 },
            fontWeight: 700,
            letterSpacing: '0.01em',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              background: `linear-gradient(120deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
              transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': {
              background: alpha(theme.palette.grey[400], 0.4),
              color: alpha(theme.palette.text.primary, 0.6),
              boxShadow: 'none',
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              Verifying...
            </>
          ) : (
            'Start Verification'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
